// Slice 11 — KPI Dashboard (tiles + 4 charts).
import { useEffect, useState } from "react";
import {
  Loader2, RefreshCw, Phone, PercentSquare, Clock4, PhoneIncoming, PhoneOutgoing,
  MessageSquare, Radio, Gauge,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { ReportChart } from "./_charts";
import { RangeControl, rangeParams, isoToday, isoDaysAgo } from "./_daterange";

const TILE_ICONS = {
  total_calls: Phone, answer_rate: PercentSquare, avg_handle: Clock4,
  inbound: PhoneIncoming, outbound: PhoneOutgoing, active_convs: MessageSquare,
  active_calls: Radio, dialer_connect: Gauge,
};

function fmtTile(t) {
  if (t.kind === "percent") return `${t.value}%`;
  if (t.kind === "seconds") {
    const s = Number(t.value || 0);
    return s >= 60 ? `${Math.round(s / 60)}m ${Math.round(s % 60)}s` : `${Math.round(s)}s`;
  }
  return Intl.NumberFormat().format(t.value || 0);
}

const RANGES = [
  { id: "7d", label: "Last 7 days" },
  { id: "30d", label: "Last 30 days" },
  { id: "90d", label: "Last 90 days" },
  { id: "today", label: "Today" },
];

function qsFor(rng, from, to) {
  const p = rangeParams(rng, from, to);
  return new URLSearchParams(p).toString();
}

export default function ReportsDashboardPage() {
  const [rng, setRng] = useState("today");
  const [from, setFrom] = useState(isoToday());
  const [to, setTo] = useState(isoToday());
  const [dash, setDash] = useState(null);
  const [charts, setCharts] = useState({});      // {reportId: {columns, rows, chart}}
  const [busy, setBusy] = useState(false);

  const QUICK_CHARTS = ["aht_by_day", "channel_mix", "inbound_vs_outbound", "agent_productivity"];

  async function load() {
    if (rng === "custom" && !(from && to)) return;
    setBusy(true);
    try {
      const rp = rangeParams(rng, from, to);
      const [dashR, ...chartRs] = await Promise.all([
        api.get(`/reports/dashboard?${qsFor(rng, from, to)}`),
        ...QUICK_CHARTS.map((id) =>
          api.post("/reports/standard/run", { report_id: id, ...rp })
        ),
      ]);
      setDash(dashR.data);
      const map = {};
      QUICK_CHARTS.forEach((id, i) => { map[id] = chartRs[i].data; });
      setCharts(map);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [rng, from, to]);

  return (
    <div className="px-4 sm:px-6 py-6 max-w-7xl" data-testid="reports-dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight">KPI Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Live KPI tiles plus 4 quick charts. All ranges computed against your CDR + dialer + conversation history.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <RangeControl rng={rng} setRng={setRng} from={from} to={to} setFrom={setFrom} setTo={setTo} testid="dash-range" />
          <Button data-testid="dash-refresh" variant="outline" onClick={load} disabled={busy} className="gap-1.5">
            {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <RefreshCw className="w-4 h-4"/>}
            Refresh
          </Button>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="dash-tiles">
        {(dash?.tiles || []).map((t) => {
          const Icon = TILE_ICONS[t.id] || Gauge;
          return (
            <Card key={t.id} className="p-4 relative overflow-hidden" data-testid={`dash-tile-${t.id}`}>
              <div className="flex items-center gap-2 text-xs text-muted-foreground font-bold uppercase tracking-wider">
                <Icon className="w-3.5 h-3.5 text-primary"/> {t.label}
              </div>
              <div className="font-display font-extrabold text-3xl mt-2 text-foreground">{fmtTile(t)}</div>
            </Card>
          );
        })}
      </div>

      {/* Quick charts */}
      <div className="grid lg:grid-cols-2 gap-5">
        {QUICK_CHARTS.map((id) => {
          const c = charts[id];
          return (
            <Card key={id} className="p-5" data-testid={`dash-chart-${id}`}>
              <h3 className="font-display font-bold mb-3">{c?.label || id}</h3>
              {c ? (
                <ReportChart kind={c.chart} columns={c.columns} rows={c.rows} />
              ) : (
                <div className="h-[260px] flex items-center justify-center">
                  <Loader2 className="w-5 h-5 animate-spin text-muted-foreground"/>
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
